/**
 * Adresa de livrare, intr-o foaie. UN SINGUR formular in toata aplicatia.
 *
 * Fluxul e cel al aplicatiilor de livrare, in trei apasari, nu noua campuri:
 *
 *  1. **Scrie strada** — sugestiile vin pe masura ce tastezi (geocodare, cu
 *     temporizare), nu dupa un buton „Gaseste". Alternativ: **locatia
 *     dispozitivului**, care nu doar da un punct, ci si completeaza strada si
 *     orasul prin geocodare inversa. Sau o **adresa recenta**, dintr-o apasare.
 *  2. **Confirma si detaliaza** — punctul ales, cu avertisment cand potrivirea
 *     e slaba, plus bloc/etaj/apartament/interfon si indicatii pentru curier.
 *  3. **Salveaza** — adresa intra in sesiune si in lista de adrese recente.
 *
 * Ce NU face componenta: nu decide nimic despre acoperire. Pune adresa in
 * sesiune si se inchide. Cine livreaza si daca se poate livra ramane raspunsul
 * core-ului, cerut de paginile care au nevoie de el.
 *
 * Coordonatele sunt obligatorii (`QuoteRequest.dropoff` cere `lat`/`lng`), iar
 * web-ul nu are voie sa le inventeze din text: o comanda cu coordonate ghicite
 * ajunge la alt bloc. De aceea pasul 2 se deschide DOAR cu un punct — venit din
 * geocodare, din browser sau dintr-o adresa recenta.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import type { DropoffAddressDto, GeoPoint } from "@deliveryapp/shared";

import { CORE_ERROR_CODES } from "@deliveryapp/shared";

import {
  ApiError,
  fetchStorefrontCoverage,
  geocodeAddress,
  locateWithBrowser,
  reverseGeocode,
  type GeocodeCandidate,
} from "../api/client";
import { dedupeCandidates, formatAddress, formatAddressDetails } from "./AddressForm";
import { matchCity, needsCityStep, pickInitialCity } from "./address-city";
import { candidateNote, candidateStreetNumber, initialMapCenter, relaxedNotice } from "./address-map";
import { forgetAddress, readRecentAddresses, rememberAddress, writeRecentAddresses } from "./address-recents";
import { isDispatcherMode } from "../dispatcher/dispatcher-session";
import { useBootstrap } from "./BootstrapProvider";
import { useCart } from "./CartProvider";
import { IconChevronRight, IconClose, IconLocate, IconPin, IconSearch, IconTrash, IconWarning } from "./icons";
import { Modal } from "./Modal";
import { PickupPicker } from "./PickupPicker";
import { Alert, Button, Field, Spinner, TextArea } from "./ui";
import { usePickupPoints } from "./usePickupPoints";

/**
 * Harta (Leaflet) intra in bundle doar cand cineva o deschide. Un chunk care nu
 * se incarca (deploy nou cu fila veche deschisa, retea cazuta) nu are voie sa
 * arunce toata aplicatia in ecranul de eroare: fereastra ramane in picioare,
 * cu celelalte doua cai (strada scrisa, locatia dispozitivului).
 */
const MapPicker = lazy(() =>
  import("./MapPicker").catch(() => ({
    default: function MapUnavailable() {
      return (
        <Alert tone="warning" title="Harta nu s-a putut încărca" icon={<IconWarning size={18} />}>
          <p>Reîncarcă pagina sau folosește locația dispozitivului; strada o poți scrie oricum la pasul următor.</p>
        </Alert>
      );
    },
  })),
);

/** Litere minime pentru a porni cautarea si pauza dupa ultima tasta. */
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 350;
/** Pauza dupa ce harta s-a oprit, inainte sa citim adresa punctului. */
const MAP_READ_DEBOUNCE_MS = 900;
/** Cat asteptam adresa punctului; dupa, punctul se confirma fara text. */
const MAP_READ_TIMEOUT_MS = 4_000;
/**
 * Sub distanta asta (in grade, ~20 m) punctul nu se reciteste: fiecare citire
 * consuma din bugetul per IP al core-ului (comun cu cautarea de adresa), iar o
 * ajustare fina a pinului nu schimba strada.
 */
const MAP_READ_MIN_DELTA_DEG = 0.0002;

/** Ce a inteles core-ul din text: localitatea, numarul si daca a cautat fara localitate. */
interface SearchMeta {
  cityHint: string | null;
  houseNumber: string | null;
  /** Strada asa cum a citit-o core-ul din text; pe calea hartii o pastram cand furnizorul n-o stie. */
  street: string | null;
  relaxed: boolean;
  /** Planurile fara localitate au rulat: doar atunci se poate spune „nici in restul zonei". */
  searchedBeyondCity: boolean;
}

type SearchState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "results"; candidates: GeocodeCandidate[]; meta: SearchMeta }
  | { kind: "no_match"; meta: SearchMeta }
  | { kind: "unavailable"; message: string };

/** Adresa citita invers pentru punctul de sub pinul hartii. */
type MapReading =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; candidate: GeocodeCandidate }
  | { kind: "none" }
  /** Core-ul a limitat citirile (buget per IP): punctul e bun, textul lipseste. */
  | { kind: "limited" }
  /** Citirea nu a raspuns la timp: nu inseamna ca nu e nicio strada aici. */
  | { kind: "timeout" };

interface Draft {
  lat: number;
  lng: number;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  building: string;
  floor: string;
  apartment: string;
  doorCode: string;
  notes: string;
  /** Potrivirea geocodarii e slaba: clientul trebuie sa confirme punctul. */
  lowConfidence: boolean;
  /** Punctul l-a pus omul pe harta: coordonatele sunt sigure, doar textul poate lipsi. */
  fromMap: boolean;
  /** Textul afisat al punctului, cum l-a dat furnizorul. */
  label: string;
  /** Core-ul a spus ca punctul NU cade in nicio zona activa a pietei. */
  outsideCoverage: boolean;
}

/** Ce se stie despre acoperire pentru PUNCTUL din schita, inainte de salvare. */
type CoveragePreview =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; deliverable: number; total: number }
  | { kind: "unknown" };

function draftFromCandidate(
  candidate: GeocodeCandidate,
  previous: DropoffAddressDto | null,
  chosenCity: string | null,
  /** Numarul scris de om in cautare; ramane cand furnizorul da doar strada. */
  typedNumber: string | null = null,
): Draft {
  return {
    lat: candidate.lat,
    lng: candidate.lng,
    street: candidate.street ?? "",
    streetNumber: candidateStreetNumber(candidate, typedNumber),
    // Localitatea ALEASA de client bate textul furnizorului cand candidatul e
    // in ea (core-ul o si scrie asa); cand furnizorul nu da niciuna, ramane
    // tot cea aleasa — un candidat din alt oras isi pastreaza orasul, marcat.
    city: candidate.cityMatch === true && chosenCity !== null ? chosenCity : (candidate.city ?? chosenCity ?? ""),
    postalCode: candidate.postalCode ?? "",
    // Detaliile pentru curier se pastreaza de la adresa anterioara doar cand e
    // ACELASI punct: blocul si interfonul de la vechea adresa n-au ce cauta la
    // cea noua.
    building: previous !== null && samePoint(previous, candidate) ? (previous.building ?? "") : "",
    floor: previous !== null && samePoint(previous, candidate) ? (previous.floor ?? "") : "",
    apartment: previous !== null && samePoint(previous, candidate) ? (previous.apartment ?? "") : "",
    doorCode: previous !== null && samePoint(previous, candidate) ? (previous.doorCode ?? "") : "",
    notes: previous !== null && samePoint(previous, candidate) ? (previous.notes ?? "") : "",
    lowConfidence: candidate.confidence === "low",
    fromMap: false,
    label: candidate.formatted,
    outsideCoverage: candidate.inCoverage === false,
  };
}

function draftFromAddress(address: DropoffAddressDto): Draft {
  return {
    lat: address.lat,
    lng: address.lng,
    street: address.street,
    streetNumber: address.streetNumber ?? "",
    city: address.city,
    postalCode: address.postalCode ?? "",
    building: address.building ?? "",
    floor: address.floor ?? "",
    apartment: address.apartment ?? "",
    doorCode: address.doorCode ?? "",
    notes: address.notes ?? "",
    lowConfidence: false,
    fromMap: false,
    label: formatAddress(address),
    outsideCoverage: false,
  };
}

function samePoint(a: { lat: number; lng: number }, b: { lat: number; lng: number }): boolean {
  return Math.abs(a.lat - b.lat) < 0.0002 && Math.abs(a.lng - b.lng) < 0.0002;
}

function toAddress(draft: Draft): DropoffAddressDto | null {
  if (draft.street.trim().length === 0 || draft.city.trim().length === 0) return null;
  return {
    lat: draft.lat,
    lng: draft.lng,
    street: draft.street.trim(),
    streetNumber: draft.streetNumber.trim() || null,
    building: draft.building.trim() || null,
    floor: draft.floor.trim() || null,
    apartment: draft.apartment.trim() || null,
    doorCode: draft.doorCode.trim() || null,
    city: draft.city.trim(),
    postalCode: draft.postalCode.trim() || null,
    notes: draft.notes.trim() || null,
  };
}

export function AddressSheet({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Motivul permite fluxurilor intrerupte sa distinga salvarea de anulare. */
  onClose: (reason?: "saved" | "dismissed") => void;
  /**
   * Ruleaza DUPA ce adresa a intrat in sesiune si INAINTE de inchidere. Nu
   * primeste adresa ca argument — cine are nevoie de ea o citeste din sesiune,
   * ca sa nu existe doua surse.
   */
  onSaved?: () => void;
}) {
  const { bootstrap, config, marketKey, t } = useBootstrap();
  const { session, setAddress, clearAddress, setPickup } = useCart();
  // Ridicarea personala apare ca a doua fila doar cand vitrina are locatii de
  // ridicare; altfel fereastra ramane exact cea de livrare.
  // Se cer dinainte, nu la deschidere: altfel fila de ridicare arata o clipa
  // „nicio locatie" pana sosesc. Raspunsul e mic si memorat 30 s de core.
  const pickupPoints = usePickupPoints();
  const [mode, setMode] = useState<"delivery" | "pickup">(session.pickup !== null ? "pickup" : "delivery");

  const [query, setQuery] = useState("");
  const [search, setSearch] = useState<SearchState>({ kind: "idle" });
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [recents, setRecents] = useState<DropoffAddressDto[]>([]);
  // Localitatea aleasa INAINTE de strada. Lista vine din zonele active ale
  // pietei (`bootstrap.cities`); un core mai vechi nu o trimite, si atunci
  // fereastra ramane pe cautarea libera de dinainte.
  const cities = useMemo(() => bootstrap.cities ?? [], [bootstrap.cities]);
  const [city, setCity] = useState<string | null>(null);
  // „Alta localitate din zona de livrare": lista pietei e a ZONELOR (un contur
  // poate acoperi si comunele din jur, sub numele orasului), nu a tuturor
  // localitatilor deservite. Cine sta in Dumbravita sub o zona „Timisoara"
  // trebuie sa poata cauta fara localitate impusa — altfel adresa lui, gasita
  // inainte, devenea negasibila. Cautarea libera ramane marginita de conturul
  // pietei (preferinta, nu limita) si spune tot daca punctul e in acoperire.
  const [anyCity, setAnyCity] = useState(false);
  const [coveragePreview, setCoveragePreview] = useState<CoveragePreview>({ kind: "idle" });
  // Harta cu pin fix, ca in aplicatiile de livrare: cand furnizorul de
  // geocodare nu cunoaste strada (OSM nu are „Strada Mierlei" in Urseni, desi
  // exista), omul pune punctul pe intrarea lui si scrie strada de mana. Fara
  // sursa de tigle din bootstrap (core vechi, sau `MAP_TILE_URL=off`), harta
  // nu se ofera deloc.
  const mapTiles = bootstrap.mapTiles ?? null;
  const [mapOpen, setMapOpen] = useState(false);
  /** Se cauta reperul de pornire (geocodarea localitatii): butonul arata asta si nu se apasa de doua ori. */
  const [mapOpening, setMapOpening] = useState(false);
  /** Localizarea ceruta DIN harta, separata de „Foloseste locatia mea" din cautare. */
  const [mapLocating, setMapLocating] = useState(false);
  const [mapCenter, setMapCenter] = useState<GeoPoint | null>(null);
  const [mapPoint, setMapPoint] = useState<GeoPoint | null>(null);
  const [mapReading, setMapReading] = useState<MapReading>({ kind: "idle" });
  const [mapError, setMapError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cityStep = draft === null && !anyCity && needsCityStep(cities, city);
  // Ultimul lucru inteles de core din text, pastrat si cand cautarea e in curs
  // sau a fost reluata: pe calea hartii numarul si strada tastate se iau de aici.
  const lastMeta = useRef<SearchMeta | null>(null);
  /**
   * Generatia foii: creste la fiecare deschidere SI la fiecare alegere a omului
   * (localitate, candidat, punct pe harta, „Schimba"). O promisiune ramasa din
   * urma (geocodarea localitatii, GPS-ul de pana la 12 s) nu mai are voie sa
   * scrie peste ce a ales omul intre timp — altfel „Foloseste locatia mea"
   * inlocuia, tarziu si in tacere, strada aleasa din lista sau punctul pus pe
   * harta cu „Pozitia dispozitivului".
   */
  const sheetGeneration = useRef(0);
  const noteChoice = (): void => {
    sheetGeneration.current += 1;
  };

  // La fiecare deschidere se porneste de la zero: cautarea veche nu are ce
  // cauta peste o adresa noua, iar lista de recente se reciteste — poate fi
  // scrisa de alta fila. Localitatea porneste de la cea a adresei curente.
  useEffect(() => {
    if (!open) return;
    sheetGeneration.current += 1;
    setQuery("");
    setSearch({ kind: "idle" });
    lastMeta.current = null;
    lastReading.current = { kind: "idle" };
    setLocationError(null);
    setDraft(null);
    setSubmitAttempted(false);
    setCoveragePreview({ kind: "idle" });
    setAnyCity(false);
    setMapOpen(false);
    setMapOpening(false);
    setMapLocating(false);
    // Localizarea pornita in foaia veche isi arunca rezultatul (generatia):
    // butonul nu ramane blocat cu spinner in foaia noua.
    setLocating(false);
    setMapCenter(null);
    setMapPoint(null);
    setMapReading({ kind: "idle" });
    setMapError(null);
    setCity(pickInitialCity(cities, session.address?.city ?? null));
    setMode(session.pickup !== null ? "pickup" : "delivery");
    // Modul dispecer: adresele vin din memoria pietei (bara dispecerului), nu din
    // „recentele" calculatorului — acolo ar fi adresele altor apelanti.
    setRecents(isDispatcherMode(marketKey) ? [] : readRecentAddresses(marketKey));
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
    // `session.address` NU e dependinta: fereastra nu are voie sa se reseteze
    // cand adresa se salveaza din ea insasi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, marketKey, cities]);

  // Dupa alegerea localitatii, campul de strada primeste focusul.
  useEffect(() => {
    if (!open || cityStep || draft !== null) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [open, cityStep, draft]);

  // Acoperirea PUNCTULUI din schita, inainte de salvare: aceeasi cerere pe care
  // o face pagina de start dupa salvare, doar ca aici raspunsul vine cat omul
  // mai poate schimba punctul, nu dupa ce a apasat „Salveaza".
  const draftLat = draft?.lat ?? null;
  const draftLng = draft?.lng ?? null;
  useEffect(() => {
    if (!open || draftLat === null || draftLng === null) {
      setCoveragePreview({ kind: "idle" });
      return;
    }
    const controller = new AbortController();
    setCoveragePreview({ kind: "loading" });
    fetchStorefrontCoverage(marketKey, { lat: draftLat, lng: draftLng }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setCoveragePreview({
          kind: "ready",
          deliverable: response.storefronts.filter((entry) => entry.status === "deliverable").length,
          total: response.storefronts.length,
        });
      })
      .catch(() => {
        // Ruta lipsa (core vechi) sau cererea picata: „nu stiu", nu „nu livram".
        if (!controller.signal.aborted) setCoveragePreview({ kind: "unknown" });
      });
    return () => controller.abort();
  }, [open, marketKey, draftLat, draftLng]);

  // Cautarea pe masura ce se scrie: temporizata, anulabila, doar de la trei
  // litere, restransa la localitatea aleasa. Fiecare raspuns se accepta doar
  // daca e al ultimei cereri.
  useEffect(() => {
    if (!open || draft !== null || cityStep) return;
    const text = query.trim();
    if (text.length < SEARCH_MIN_CHARS) {
      // Text sters sau localitate schimbata: ce intelesese core-ul din cautarea
      // veche nu mai are ce cauta in schita facuta pe harta.
      lastMeta.current = null;
      setSearch({ kind: "idle" });
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setSearch({ kind: "searching" });
      geocodeAddress(marketKey, city === null ? { query: text } : { query: text, city }, controller.signal)
        .then((response) => {
          if (controller.signal.aborted) return;
          const candidates = dedupeCandidates(response.candidates);
          // Un core mai vechi nu spune ce a inteles: localitatea ramane cea
          // aleasa, numarul nu se pastreaza, iar „relaxat" nu exista.
          const meta: SearchMeta = {
            cityHint: response.cityHint ?? city,
            houseNumber: response.houseNumber ?? null,
            street: response.street ?? null,
            relaxed: response.relaxed === true,
            searchedBeyondCity: response.searchedBeyondCity === true,
          };
          lastMeta.current = meta;
          setSearch(candidates.length === 0 ? { kind: "no_match", meta } : { kind: "results", candidates, meta });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setSearch({
            kind: "unavailable",
            message: error instanceof ApiError ? error.message : "Căutarea adresei nu a putut fi efectuată.",
          });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, open, marketKey, draft, city, cityStep]);

  const pickCandidate = useCallback(
    (candidate: GeocodeCandidate) => {
      sheetGeneration.current += 1;
      const typedNumber = search.kind === "results" ? search.meta.houseNumber : null;
      setDraft(draftFromCandidate(candidate, session.address, city, typedNumber));
      setSubmitAttempted(false);
    },
    [session.address, city, search],
  );

  const useLocation = async (): Promise<void> => {
    const generation = sheetGeneration.current;
    setLocating(true);
    setLocationError(null);
    try {
      const position = await locateWithBrowser();
      if (generation !== sheetGeneration.current) return;
      // Geocodarea inversa completeaza strada si orasul. Cand lipseste (core
      // vechi) sau nu gaseste nimic, pasul 2 se deschide cu punctul si campurile
      // goale: clientul scrie strada de mana, dar nu mai are nevoie de harta.
      let candidate: GeocodeCandidate = {
        lat: position.lat,
        lng: position.lng,
        formatted: "Poziția dispozitivului",
        confidence: "low",
      };
      let locatedCity: string | null = null;
      try {
        const reverse = await reverseGeocode(marketKey, position);
        if (generation !== sheetGeneration.current) return;
        const found = reverse.candidates[0];
        if (found !== undefined) {
          // Localitatea de la GPS, in scrierea pietei cand e una de-a ei: asa
          // se sare si peste pasul de alegere, si peste retastarea ei.
          locatedCity = matchCity(cities, found.city);
          candidate = {
            ...found,
            lat: position.lat,
            lng: position.lng,
            cityMatch: locatedCity !== null ? true : found.cityMatch ?? null,
          };
        }
      } catch {
        // Fara text: ramane punctul, iar pasul 2 cere strada.
      }
      if (generation !== sheetGeneration.current) return;
      if (locatedCity !== null) {
        setCity(locatedCity);
        setAnyCity(false);
      }
      setDraft(draftFromCandidate(candidate, session.address, locatedCity ?? city));
    } catch (error: unknown) {
      if (generation !== sheetGeneration.current) return;
      setLocationError(error instanceof Error ? error.message : "Locația nu a putut fi determinată.");
    } finally {
      setLocating(false);
    }
  };

  // Adresa punctului de sub pin, la fiecare oprire a hartii: temporizata,
  // anulabila, doar ultimul raspuns conteaza. Lipsa unei strazi cunoscute nu e
  // o eroare — punctul ramane bun, iar strada se scrie la pasul urmator.
  /** Ultimul punct pentru care s-a CERUT adresa; sub ~20 m de el nu se reciteste. */
  const lastReadPoint = useRef<GeoPoint | null>(null);
  /** Ultima stare ASEZATA a citirii (ready/none/limited/timeout): la „prea aproape" se revine la ea, nu la `loading`. */
  const lastReading = useRef<MapReading>({ kind: "idle" });
  /**
   * Cererea de citire in zbor. NU se anuleaza la o mutare mica (zoom, ajustare
   * sub ~20 m): raspunsul ei e pentru un punct la cativa metri, adica exact ce
   * presupune pragul. Se anuleaza doar cand porneste o cerere noua (mutare
   * mare) sau cand harta se inchide.
   */
  const inflightRead = useRef<{ controller: AbortController; abortTimer: number } | null>(null);
  /** Pinul de ACUM, citit din ref in raspunsuri: un raspuns pentru alt punct nu se aseaza. */
  const currentPoint = useRef<GeoPoint | null>(null);
  currentPoint.current = mapPoint;
  const cancelInflightRead = useCallback((): void => {
    const current = inflightRead.current;
    if (current === null) return;
    window.clearTimeout(current.abortTimer);
    current.controller.abort();
    inflightRead.current = null;
  }, []);
  useEffect(() => {
    if (!mapOpen) cancelInflightRead();
  }, [mapOpen, cancelInflightRead]);
  useEffect(() => cancelInflightRead, [cancelInflightRead]);

  useEffect(() => {
    if (!open || !mapOpen || mapPoint === null) return;
    const previous = lastReadPoint.current;
    if (
      previous !== null &&
      Math.abs(previous.lat - mapPoint.lat) < MAP_READ_MIN_DELTA_DEG &&
      Math.abs(previous.lng - mapPoint.lng) < MAP_READ_MIN_DELTA_DEG
    ) {
      // Cererea in zbor (daca exista) continua si va aseza starea; altfel se
      // revine la ultima stare asezata — fara asta butonul ramanea gri pe veci.
      if (inflightRead.current === null) setMapReading(lastReading.current);
      return;
    }
    setMapReading({ kind: "loading" });
    const timer = window.setTimeout(() => {
      // Cererea veche (alt punct) e depasita: o anulam abia acum, cand porneste
      // una noua, nu la fiecare oprire a hartii. Daca intre timp ea a raspuns
      // pentru punctul vechi, starea e din nou `loading` pentru cel nou.
      setMapReading({ kind: "loading" });
      cancelInflightRead();
      const requested = mapPoint;
      lastReadPoint.current = mapPoint;
      const controller = new AbortController();
      // Timeout-ul e singurul abort care are voie sa scrie in stare: celelalte
      // inseamna ca alt punct (sau harta inchisa) a preluat starea.
      let timedOut = false;
      // Citirea e un ajutor, nu o conditie: pe o legatura slaba nu tine
      // „Confirma punctul" blocat mai mult de cateva secunde.
      const abortTimer = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, MAP_READ_TIMEOUT_MS);
      const entry = { controller, abortTimer };
      inflightRead.current = entry;
      const settle = (reading: MapReading): void => {
        // Raspunsul e pentru PINUL de acum? Cererea veche nu se anuleaza la
        // mutari mici, dar la o mutare mare raspunsul ei ar fi textul altui
        // loc pe coordonatele pinului nou: ramane `loading` pana vine al lui.
        const now = currentPoint.current;
        if (
          now === null ||
          Math.abs(now.lat - requested.lat) >= MAP_READ_MIN_DELTA_DEG ||
          Math.abs(now.lng - requested.lng) >= MAP_READ_MIN_DELTA_DEG
        ) {
          return;
        }
        lastReading.current = reading;
        setMapReading(reading);
      };
      const settleTimeout = (): void => {
        // Fara raspuns nu se afirma „nu e nicio strada aici"; o mutare
        // ulterioara reincearca, fiindca punctul nu mai e „citit".
        lastReadPoint.current = null;
        settle({ kind: "timeout" });
      };
      reverseGeocode(marketKey, mapPoint, controller.signal)
        .then((response) => {
          if (controller.signal.aborted) {
            // Abort-ul de timeout picat dupa ce fetch-ul a raspuns: tot timeout e.
            if (timedOut) settleTimeout();
            return;
          }
          const found = response.candidates[0];
          settle(found === undefined ? { kind: "none" } : { kind: "ready", candidate: found });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted && !timedOut) return;
          if (timedOut) {
            settleTimeout();
            return;
          }
          // Limitarea de rata nu inseamna „nu e nicio strada aici": se spune ce e.
          const limited = error instanceof ApiError && error.code === CORE_ERROR_CODES.RATE_LIMITED;
          settle(limited ? { kind: "limited" } : { kind: "none" });
        })
        .finally(() => {
          window.clearTimeout(abortTimer);
          if (inflightRead.current === entry) inflightRead.current = null;
        });
    }, MAP_READ_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, mapOpen, mapPoint, marketKey, cancelInflightRead]);

  /**
   * Deschide harta cat mai aproape de om: punctul schitei (ajustare), primul
   * candidat gasit, adresa din sesiune, centrul pietei; in lipsa tuturor,
   * localitatea aleasa, geocodata. Fara niciun reper harta nu se deschide
   * oarba pe o tara intreaga — se spune de ce si ce alternative raman.
   */
  const openMap = async (): Promise<void> => {
    if (mapOpening) return;
    // O alegere noua: un GPS pornit inainte nu mai are voie sa scrie peste harta.
    noteChoice();
    const generation = sheetGeneration.current;
    setMapError(null);
    setMapOpening(true);
    try {
      // Un candidat RELAXAT e in alta localitate decat cea ceruta: harta nu
      // porneste de acolo, ci de la sesiune/piata/localitatea aleasa.
      const firstCandidate = search.kind === "results" && !search.meta.relaxed ? (search.candidates[0] ?? null) : null;
      let center = initialMapCenter({
        draft,
        candidate: firstCandidate,
        session: session.address,
        coverageCenter: bootstrap.coverageCenter ?? null,
      });
      if (center === null && city !== null) {
        try {
          const found = await geocodeAddress(marketKey, { query: city });
          center = found.candidates[0] ?? null;
        } catch {
          center = null;
        }
      }
      if (generation !== sheetGeneration.current) return;
      if (center === null) {
        // Se vede LANGA buton (nu doar in sectiunea hartii, care nu se deschide):
        // altfel apasarea n-ar avea nicio reactie.
        setMapError(
          t("home.address_map_no_start", "Harta nu are de unde să pornească. Folosește locația dispozitivului sau scrie strada."),
        );
        return;
      }
      const start = { lat: center.lat, lng: center.lng };
      lastReadPoint.current = null;
      lastReading.current = { kind: "idle" };
      setMapCenter(start);
      // Punctul il anunta harta cand e gata (`MapPicker` emite la pornire):
      // pana atunci nu e nimic de confirmat — un chunk cazut nu are voie sa
      // lase „Confirma punctul" activ pe centrul pietei.
      setMapPoint(null);
      setMapReading({ kind: "idle" });
      setMapOpen(true);
    } finally {
      setMapOpening(false);
    }
  };

  const locateOnMap = async (): Promise<void> => {
    noteChoice();
    const generation = sheetGeneration.current;
    setMapLocating(true);
    setMapError(null);
    try {
      const position = await locateWithBrowser();
      if (generation !== sheetGeneration.current) return;
      // Obiect NOU la fiecare cerere: harta se recentreaza si cand coordonatele
      // sunt identice cu cele dinainte (vezi `MapPicker`).
      setMapCenter({ lat: position.lat, lng: position.lng });
      setMapPoint({ lat: position.lat, lng: position.lng });
    } catch (error: unknown) {
      if (generation !== sheetGeneration.current) return;
      setMapError(error instanceof Error ? error.message : "Locația nu a putut fi determinată.");
    } finally {
      setMapLocating(false);
    }
  };

  /**
   * Punctul de sub pin devine adresa: coordonatele sunt ALE PUNCTULUI (curierul
   * merge unde a pus omul pinul), textul e al furnizorului cand exista. La
   * ajustarea din pasul de detalii se muta doar punctul; ce a scris omul ramane.
   */
  const confirmMapPoint = (): void => {
    if (mapPoint === null) return;
    noteChoice();
    const found = mapReading.kind === "ready" ? mapReading.candidate : null;
    const locatedCity = found === null ? null : matchCity(cities, found.city);
    const base: GeocodeCandidate =
      found === null
        ? { lat: mapPoint.lat, lng: mapPoint.lng, formatted: t("home.address_map_point", "Punct ales pe hartă"), confidence: "low" }
        : { ...found, lat: mapPoint.lat, lng: mapPoint.lng, cityMatch: locatedCity !== null ? true : (found.cityMatch ?? null) };
    // Ce a scris omul in cautare ramane: numarul mereu (furnizorul rar il stie
    // in sate), iar strada cand cautarea n-a gasit-o in localitatea ceruta —
    // atunci drumul cel mai apropiat de pe harta e mai probabil gresit decat
    // ce a tastat el. Meta e cea din ultimul raspuns, nu din starea curenta a
    // cautarii (care poate fi „searching" chiar acum).
    const meta = lastMeta.current;
    const typedNumber = meta?.houseNumber ?? null;
    const streetUnknownHere = meta !== null && (search.kind === "no_match" || meta.relaxed);
    const typedStreet = streetUnknownHere ? meta.street : null;
    if (draft !== null) {
      setDraft({
        ...draft,
        lat: mapPoint.lat,
        lng: mapPoint.lng,
        label: base.formatted,
        lowConfidence: false,
        fromMap: true,
        outsideCoverage: base.inCoverage === false,
        street: draft.street.trim().length > 0 ? draft.street : (found?.street ?? ""),
        city: draft.city.trim().length > 0 ? draft.city : (locatedCity ?? found?.city ?? city ?? ""),
      });
    } else {
      if (locatedCity !== null) {
        setCity(locatedCity);
        setAnyCity(false);
      }
      const next = draftFromCandidate(base, session.address, locatedCity ?? city, typedNumber);
      setDraft({
        ...next,
        lowConfidence: false,
        fromMap: true,
        street: typedStreet !== null && typedStreet.length > 0 ? typedStreet : next.street,
        // Pe harta coordonatele sunt ale PINULUI, nu ale imobilului gasit de
        // furnizor: numarul lui („10" de pe strada vecina) nu bate numarul
        // scris de om. Pe calea cautarii ramane invers (candidatul E imobilul).
        streetNumber: typedNumber !== null && typedNumber.length > 0 ? typedNumber : next.streetNumber,
      });
    }
    setMapOpen(false);
    setSubmitAttempted(false);
  };

  const save = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (draft === null) return;
    setSubmitAttempted(true);
    const address = toAddress(draft);
    if (address === null) return;
    setAddress(address);
    if (!isDispatcherMode(marketKey)) writeRecentAddresses(marketKey, rememberAddress(readRecentAddresses(marketKey), address));
    onSaved?.();
    onClose("saved");
  };

  const removeRecent = (address: DropoffAddressDto): void => {
    const next = forgetAddress(recents, address);
    setRecents(next);
    writeRecentAddresses(marketKey, next);
  };

  const streetError = submitAttempted && draft !== null && draft.street.trim().length === 0 ? "Completează strada." : null;
  const cityError = submitAttempted && draft !== null && draft.city.trim().length === 0 ? "Completează localitatea." : null;
  const offersPickup = pickupPoints.points.length > 0 || session.pickup !== null;
  const pickupMode = offersPickup && mode === "pickup";
  const title = pickupMode ? t("home.pickup_title", "De unde ridici?") : t("home.address_title", "Unde livrăm?");
  const recentsToShow = useMemo(
    () => recents.filter((item) => session.address === null || !samePoint(item, session.address) || (item.apartment ?? "") !== (session.address.apartment ?? "")),
    [recents, session.address],
  );

  /** Adresa recenta intra direct in sesiune; localitatea ei devine cea aleasa. */
  const useRecent = (address: DropoffAddressDto): void => {
    noteChoice();
    setAddress(address);
    if (!isDispatcherMode(marketKey)) writeRecentAddresses(marketKey, rememberAddress(recents, address));
    onSaved?.();
    onClose("saved");
  };

  const recentsSection =
    recentsToShow.length === 0 ? null : (
      <section aria-labelledby="adrese-recente-titlu" className="flex flex-col gap-2">
        <h3 id="adrese-recente-titlu" className="text-xs font-bold uppercase tracking-wide text-ink-muted">
          Adrese recente
        </h3>
        <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface">
          {recentsToShow.map((address) => (
            <li key={`${address.lat},${address.lng},${address.apartment ?? ""}`} className="flex items-center">
              <button
                type="button"
                onClick={() => useRecent(address)}
                className="da-tap flex min-w-0 flex-1 items-start gap-3 px-3.5 py-3 text-left hover:bg-surface-hover"
              >
                <IconPin size={20} className="mt-0.5 shrink-0 text-ink-muted" />
                <span className="min-w-0">
                  <span className="da-clamp-1 block text-sm font-semibold text-ink">{formatAddress(address)}</span>
                  {formatAddressDetails(address).length === 0 ? null : (
                    <span className="block text-xs text-ink-muted">{formatAddressDetails(address)}</span>
                  )}
                </span>
              </button>
              <button
                type="button"
                onClick={() => removeRecent(address)}
                aria-label={`Șterge din recente: ${formatAddress(address)}`}
                className="grid h-11 w-11 shrink-0 place-items-center text-ink-muted hover:text-danger"
              >
                <IconTrash size={18} />
              </button>
            </li>
          ))}
        </ul>
      </section>
    );

  const locateButton = (
    <>
      <Button
        variant="secondary"
        block
        icon={<IconLocate size={20} />}
        loading={locating}
        onClick={() => void useLocation()}
      >
        Folosește locația mea
      </Button>
      {locationError === null ? null : (
        <Alert tone="warning" title="Locația dispozitivului nu a fost obținută">
          <p>{locationError}</p>
          <p className="mt-1">Scrie adresa mai sus — e o cale separată, care nu depinde de locație.</p>
        </Alert>
      )}
    </>
  );

  const mapButton =
    mapTiles === null ? null : (
      <>
        <Button variant="secondary" block icon={<IconPin size={20} />} loading={mapOpening} onClick={() => void openMap()}>
          {t("home.address_map_pick", "Alege punctul pe hartă")}
        </Button>
        {mapOpen || mapError === null ? null : (
          <Alert tone="warning" title="Harta nu a putut fi deschisă" icon={<IconWarning size={18} />}>
            <p>{mapError}</p>
          </Alert>
        )}
      </>
    );

  const mapSection =
    mapTiles === null ? null : (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-muted">
          {t(
            "home.address_map_hint",
            "Mută harta până când punctul ajunge pe intrarea ta. Strada se completează singură când o cunoaștem; altfel o scrii la pasul următor.",
          )}
        </p>
        {mapCenter === null ? null : (
          <Suspense
            fallback={
              <div className="grid h-[320px] place-items-center rounded-card border border-line bg-surface">
                <Spinner size={24} />
              </div>
            }
          >
            <MapPicker center={mapCenter} tiles={mapTiles} onMove={setMapPoint} />
          </Suspense>
        )}
        <div className="flex items-start gap-2 rounded-card border border-line bg-surface px-3.5 py-2.5 text-sm" role="status">
          <IconPin size={18} className="mt-0.5 shrink-0 text-brand-on-surface" />
          {mapReading.kind === "loading" ? (
            <span className="text-ink-muted">Se citește adresa punctului…</span>
          ) : mapReading.kind === "ready" ? (
            <span className="min-w-0">
              <span className="block font-semibold text-ink">{mapReading.candidate.formatted}</span>
              {mapReading.candidate.inCoverage === false ? (
                <span className="block text-xs font-semibold text-warning">În afara zonei de livrare</span>
              ) : mapReading.candidate.inCoverage === true ? (
                <span className="block text-xs font-semibold text-success">În zona de livrare</span>
              ) : null}
            </span>
          ) : mapReading.kind === "none" ? (
            <span className="text-ink-muted">Nu cunoaștem o stradă exact aici — o scrii la pasul următor.</span>
          ) : mapReading.kind === "limited" ? (
            <span className="text-ink-muted">Prea multe citiri ale hărții într-un minut. Punctul rămâne bun; strada o scrii la pasul următor.</span>
          ) : mapReading.kind === "timeout" ? (
            <span className="text-ink-muted">Citirea adresei a durat prea mult. Punctul rămâne bun; mută puțin harta ca să reîncercăm sau scrie strada la pasul următor.</span>
          ) : (
            <span className="text-ink-muted">Mută harta ca să citim adresa punctului.</span>
          )}
        </div>
        {mapError === null ? null : (
          <Alert tone="warning" title="Harta nu a putut fi poziționată" icon={<IconWarning size={18} />}>
            <p>{mapError}</p>
          </Alert>
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={<IconLocate size={18} />} loading={mapLocating} onClick={() => void locateOnMap()}>
            Locația mea
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setMapOpen(false);
              setMapError(null);
            }}
          >
            Înapoi
          </Button>
        </div>
      </div>
    );

  return (
    <Modal
      open={open}
      onClose={() => onClose("dismissed")}
      title={mapOpen ? t("home.address_map_title", "Alege punctul pe hartă") : draft === null ? title : "Detalii pentru curier"}
      footer={
        pickupMode ? undefined : mapOpen ? (
          // `key` diferit de butonul de salvare: la confirmare React ar refolosi
          // acelasi <button>, i-ar schimba pe loc `type` in `submit` si
          // `form` in formularul de detalii, iar actiunea implicita a ACELUIASI
          // click — evaluata dupa handler — ar trimite formularul: adresa se
          // salva fara ca omul sa vada pasul de detalii. Masurat in browser.
          <Button
            key="confirma-punctul"
            type="button"
            block
            size="lg"
            onClick={confirmMapPoint}
            disabled={mapPoint === null || mapReading.kind === "loading"}
          >
            {t("home.address_map_confirm", "Confirmă punctul")}
          </Button>
        ) : draft === null ? undefined : (
          <Button key="salveaza-adresa" type="submit" form="formular-adresa" block size="lg">
            {t("home.address_submit", "Salvează adresa")}
          </Button>
        )
      }
    >
      {!offersPickup || mapOpen || draft !== null ? null : (
        <div className="mb-4 grid grid-cols-2 gap-1 rounded-full bg-surface p-1" role="tablist" aria-label="Cum primești comanda">
          {(
            [
              ["delivery", "Livrare"],
              ["pickup", "Ridicare personală"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              onClick={() => setMode(value)}
              className={[
                "da-tap rounded-full px-3 py-2 text-sm font-bold transition-colors",
                mode === value ? "bg-canvas text-ink shadow-sm" : "text-ink-muted hover:text-ink",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {pickupMode ? (
        <PickupPicker
          points={pickupPoints.points}
          loading={pickupPoints.loading}
          selectedVendorId={session.pickup?.vendorId ?? null}
          onPick={(point) => {
            setPickup(point);
            onSaved?.();
            onClose("saved");
          }}
        />
      ) : mapOpen && mapSection !== null ? (
        mapSection
      ) : cityStep ? (
        // Pasul 0: localitatea. Lista e a pietei (zonele ei active), deci
        // clientul din alt oras afla aici ca nu se livreaza la el, nu dupa ce
        // si-a scris strada, numarul, blocul si interfonul.
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-muted">Alege localitatea, apoi scrie strada. Acestea sunt localitățile în care livrăm cel mai des.</p>
          <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface" aria-label="Localități în care livrăm">
            {cities.map((item) => (
              <li key={item}>
                <button
                  type="button"
                  onClick={() => {
                    noteChoice();
                    setCity(item);
                    setAnyCity(false);
                    setQuery("");
                    setSearch({ kind: "idle" });
                  }}
                  className="da-tap flex w-full items-center gap-3 px-3.5 py-3 text-left hover:bg-surface-hover"
                >
                  <IconPin size={20} className="shrink-0 text-brand-on-surface" />
                  <span className="min-w-0 flex-1 text-sm font-semibold text-ink">{item}</span>
                  <IconChevronRight size={18} className="shrink-0 text-ink-muted" />
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => {
                  noteChoice();
                  setCity(null);
                  setAnyCity(true);
                  setQuery("");
                  setSearch({ kind: "idle" });
                }}
                className="da-tap flex w-full items-center gap-3 px-3.5 py-3 text-left hover:bg-surface-hover"
              >
                <IconSearch size={20} className="shrink-0 text-ink-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">Altă localitate din zona de livrare</span>
                  <span className="block text-xs text-ink-muted">Cauți strada fără localitate; îți spunem dacă ajungem acolo.</span>
                </span>
                <IconChevronRight size={18} className="shrink-0 text-ink-muted" />
              </button>
            </li>
          </ul>
          {locateButton}
          {mapButton}
          {recentsSection}
        </div>
      ) : draft === null ? (
        <div className="flex flex-col gap-4">
          {city === null && !anyCity ? null : (
            <div className="flex items-center gap-2 rounded-card border border-line bg-surface px-3.5 py-2.5">
              <IconPin size={18} className="shrink-0 text-brand-on-surface" />
              <span className="min-w-0 flex-1 text-sm text-ink">
                <span className="text-ink-muted">Localitate: </span>
                <span className="font-semibold">{city ?? "oricare din zona de livrare"}</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  noteChoice();
                  // Cu o singura localitate in lista nu exista pas de alegere:
                  // „Schimba" trece direct pe cautarea fara localitate, si invers.
                  if (cities.length > 1) {
                    setCity(null);
                    setAnyCity(false);
                  } else if (anyCity) {
                    setAnyCity(false);
                    setCity(cities[0] ?? null);
                  } else {
                    setCity(null);
                    setAnyCity(true);
                  }
                  setQuery("");
                  setSearch({ kind: "idle" });
                }}
              >
                {cities.length > 1 ? "Schimbă" : anyCity ? (cities[0] ?? "Schimbă") : "Altă localitate"}
              </Button>
            </div>
          )}
          <div className="relative">
            <label htmlFor="cauta-adresa" className="sr-only">
              Strada și numărul
            </label>
            <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-ink-muted">
              {search.kind === "searching" ? <Spinner size={18} /> : <IconSearch size={20} />}
            </span>
            <input
              ref={inputRef}
              id="cauta-adresa"
              type="search"
              autoComplete="street-address"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Strada și numărul, ex. Alba Iulia 4"
              className="w-full rounded-control border border-line bg-canvas py-3.5 pl-11 pr-10 text-base text-ink placeholder:text-ink-muted focus:border-brand"
            />
            {query.length === 0 ? null : (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Șterge textul"
                className="absolute inset-y-0 right-2 grid w-9 place-items-center text-ink-muted hover:text-ink"
              >
                <IconClose size={18} />
              </button>
            )}
          </div>

          {search.kind === "results" ? (
            <>
              {relaxedNotice(search.meta.cityHint, search.meta.relaxed) === null ? null : (
                <p className="text-sm text-ink-muted" role="status">
                  {relaxedNotice(search.meta.cityHint, search.meta.relaxed)}
                </p>
              )}
              <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface" aria-label="Adrese găsite">
                {search.candidates.map((candidate) => {
                  const note = candidateNote(candidate, search.meta.cityHint);
                  // Numarul scris de om se vede deja pe rand, ca sa nu para pierdut.
                  const typedNumber = candidate.streetNumber ? null : search.meta.houseNumber;
                  return (
                    <li key={`${candidate.lat},${candidate.lng},${candidate.formatted}`}>
                      <button
                        type="button"
                        onClick={() => pickCandidate(candidate)}
                        className="da-tap flex w-full items-start gap-3 px-3.5 py-3 text-left hover:bg-surface-hover"
                      >
                        <IconPin size={20} className="mt-0.5 shrink-0 text-brand-on-surface" />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-ink">{candidate.formatted}</span>
                          {note === null ? null : (
                            <span className={`block text-xs font-semibold ${note.tone === "warning" ? "text-warning" : "text-success"}`}>
                              {note.text}
                            </span>
                          )}
                          {candidate.confidence === "high" ? null : (
                            <span className="block text-xs text-ink-muted">
                              {candidate.confidence === "medium"
                                ? typedNumber !== null && typedNumber.length > 0
                                  ? `Numărul ${typedNumber} nu e pe hartă — îl păstrăm așa cum l-ai scris`
                                  : "Fără numărul clădirii — îl completezi la pasul următor"
                                : "Potrivire aproximativă"}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : search.kind === "no_match" ? (
            <div className="flex flex-col gap-2 rounded-card border border-line bg-surface px-3.5 py-3 text-sm text-ink">
              <p>
                {search.meta.cityHint === null
                  ? t(
                      "home.address_no_match",
                      "Nu am găsit strada. Scrie doar numele străzii — fără localitate, bloc sau apartament — și verifică scrierea.",
                    )
                  : search.meta.searchedBeyondCity
                    ? `Nu am găsit strada în ${search.meta.cityHint} și nici în restul zonei de livrare. Verifică scrierea (doar numele străzii).`
                    : `Nu am găsit strada în ${search.meta.cityHint}. Verifică scrierea (doar numele străzii).`}
              </p>
              {mapTiles === null ? null : (
                <p className="text-ink-muted">
                  {t("home.address_no_match_map", "Strada nu apare pe hartă? Alege punctul direct pe hartă, iar strada o scrii tu.")}
                </p>
              )}
            </div>
          ) : search.kind === "unavailable" ? (
            <Alert tone="warning" title="Căutarea adresei nu a răspuns" icon={<IconWarning size={18} />}>
              <p>{search.message}</p>
              <p className="mt-1">Poți folosi locația dispozitivului, care nu depinde de căutare.</p>
            </Alert>
          ) : query.trim().length > 0 && query.trim().length < SEARCH_MIN_CHARS ? (
            <p className="text-xs text-ink-muted">Scrie cel puțin {SEARCH_MIN_CHARS} litere.</p>
          ) : null}

          {locateButton}
          {mapButton}

          {session.address === null ? null : (
            <div className="rounded-card border border-brand bg-brand-soft p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-brand-on-surface">Adresa curentă</p>
              <p className="mt-0.5 text-sm font-semibold text-ink">{formatAddress(session.address)}</p>
              {formatAddressDetails(session.address).length === 0 ? null : (
                <p className="text-xs text-ink-muted">{formatAddressDetails(session.address)}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                {session.pickup === null ? null : (
                  // Clientul era pe ridicare: intoarcerea la livrare la adresa
                  // retinuta e un singur gest, nu o cautare noua.
                  <Button
                    size="sm"
                    onClick={() => {
                      setAddress(session.address as DropoffAddressDto);
                      onSaved?.();
                      onClose("saved");
                    }}
                  >
                    Livrează aici
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    noteChoice();
                    setDraft(draftFromAddress(session.address as DropoffAddressDto));
                  }}
                >
                  Modifică detaliile
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    clearAddress();
                    onClose("dismissed");
                  }}
                >
                  Șterge adresa
                </Button>
              </div>
            </div>
          )}

          {recentsSection}

          {config.identity.supportPhone === undefined || config.identity.supportPhone.length === 0 ? null : (
            <p className="text-xs text-ink-muted">
              Nu găsești adresa? Poți comanda telefonic la{" "}
              <a className="font-semibold underline" href={`tel:${config.identity.supportPhone.replace(/\s+/g, "")}`}>
                {config.identity.supportPhone}
              </a>
              .
            </p>
          )}
        </div>
      ) : (
        <form id="formular-adresa" onSubmit={save} className="flex flex-col gap-4" noValidate>
          <div className="flex items-start gap-3 rounded-card border border-line bg-surface p-3">
            <IconPin size={22} className="mt-0.5 shrink-0 text-brand-on-surface" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink">{draft.label}</p>
              <p className="text-xs text-ink-muted">Punctul pe care merge curierul.</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              {mapTiles === null ? null : (
                <Button size="sm" variant="ghost" onClick={() => void openMap()}>
                  {t("home.address_map_adjust", "Ajustează pe hartă")}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  noteChoice();
                  setDraft(null);
                }}
              >
                Schimbă
              </Button>
            </div>
          </div>

          {draft.fromMap ? (
            <Alert tone="info" title="Punctul e cel ales de tine pe hartă" icon={<IconPin size={18} />}>
              Scrie strada și numărul, ca să găsească curierul intrarea.
            </Alert>
          ) : draft.lowConfidence ? (
            <Alert tone="warning" title="Verifică punctul ales" icon={<IconWarning size={18} />}>
              Potrivirea nu e sigură. Completează strada și numărul exact, ca să nu trimitem curierul la altă clădire.
            </Alert>
          ) : null}

          {/* Acoperirea punctului, INAINTE de salvare: daca nu ajunge nimeni
              aici, omul afla acum, cat mai poate schimba punctul. Salvarea
              ramane posibila — poate a ales gresit strada, poate revine mai
              tarziu — iar pagina de start ii va spune acelasi lucru. */}
          {draft.outsideCoverage || (coveragePreview.kind === "ready" && coveragePreview.deliverable === 0) ? (
            <Alert tone="warning" title="Momentan nu livrăm la acest punct" icon={<IconWarning size={18} />}>
              {draft.outsideCoverage
                ? "Adresa e în afara zonelor noastre de livrare. Verifică strada aleasă sau încearcă altă adresă."
                : "Zona e acoperită, dar niciun restaurant nu poate livra aici acum. Poți salva adresa și reveni mai târziu."}
            </Alert>
          ) : coveragePreview.kind === "ready" ? (
            <p className="flex items-center gap-2 rounded-card border border-line bg-surface px-3.5 py-2.5 text-sm font-semibold text-success" role="status">
              <span aria-hidden="true">✓</span>
              {coveragePreview.deliverable === 1
                ? "Un restaurant livrează la acest punct"
                : `${coveragePreview.deliverable} restaurante livrează la acest punct`}
            </p>
          ) : coveragePreview.kind === "loading" ? (
            <p className="flex items-center gap-2 text-sm text-ink-muted" role="status">
              <Spinner size={16} /> Verificăm cine livrează aici…
            </p>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-[2fr,1fr]">
            <Field
              label="Stradă"
              required
              autoComplete="address-line1"
              autoCorrect="off"
              value={draft.street}
              error={streetError}
              onChange={(event) => setDraft({ ...draft, street: event.target.value })}
            />
            <Field
              label="Număr"
              quiet
              autoComplete="address-line2"
              inputMode="numeric"
              value={draft.streetNumber}
              onChange={(event) => setDraft({ ...draft, streetNumber: event.target.value })}
            />
          </div>
          <Field
            label="Localitate"
            required
            autoComplete="address-level2"
            value={draft.city}
            error={cityError}
            onChange={(event) => setDraft({ ...draft, city: event.target.value })}
          />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Bloc" quiet value={draft.building} onChange={(event) => setDraft({ ...draft, building: event.target.value })} />
            <Field label="Etaj" quiet value={draft.floor} onChange={(event) => setDraft({ ...draft, floor: event.target.value })} />
            <Field label="Apartament" quiet value={draft.apartment} onChange={(event) => setDraft({ ...draft, apartment: event.target.value })} />
            <Field label="Interfon" quiet value={draft.doorCode} onChange={(event) => setDraft({ ...draft, doorCode: event.target.value })} />
          </div>
          <TextArea
            label="Indicații pentru curier"
            hint="Intrare, reper, la ce ușă să sune, dacă lași comanda la ușă."
            rows={2}
            value={draft.notes}
            onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
          />
        </form>
      )}
    </Modal>
  );
}
